/**
 * Post-Spin Service
 * Manages post-spin state, localStorage persistence, and business logic
 */

import type { PostSpinActivity } from '../components/post-spin/post-spin-view-manager.js';

export interface SpinData {
  timestamp: number;
  activity: PostSpinActivity;
}

export class PostSpinService {
  private static instance: PostSpinService;
  private readonly STORAGE_KEY = 'goali_last_wheel_spin';
  private readonly ENJOY_DURATION_MS = 10 * 60 * 1000; // 10 minutes

  static getInstance(): PostSpinService {
    if (!PostSpinService.instance) {
      PostSpinService.instance = new PostSpinService();
    }
    return PostSpinService.instance;
  }

  /**
   * Store activity spin data in localStorage
   */
  storeSpinData(activity: PostSpinActivity): void {
    const spinData: SpinData = {
      timestamp: Date.now(),
      activity: {
        id: activity.id,
        name: activity.name,
        description: activity.description,
        domain: activity.domain,
        type: activity.type,
        icon: activity.icon,
        base_challenge_rating: activity.base_challenge_rating,
        duration_range: activity.duration_range,
        instructions: activity.instructions,
        requirements: activity.requirements,
        materials: activity.materials,
        activity_tailored_id: activity.activity_tailored_id,
        wheel_item_id: activity.wheel_item_id
      }
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(spinData));
    } catch (error) {
      console.warn('Failed to store wheel spin data:', error);
    }
  }

  /**
   * Get stored spin data from localStorage
   */
  getStoredSpinData(): SpinData | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        return JSON.parse(data);
      }
    } catch (error) {
      console.warn('Failed to retrieve wheel spin data:', error);
      this.clearStoredSpinData();
    }
    return null;
  }

  /**
   * Clear stored spin data from localStorage
   */
  clearStoredSpinData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear wheel spin data:', error);
    }
  }

  /**
   * Check if there's an active enjoy overlay session
   */
  checkForActiveEnjoyOverlay(): { shouldShowEnjoy: boolean; shouldShowFeedback: boolean; activity: PostSpinActivity | null } {
    const spinData = this.getStoredSpinData();
    if (!spinData) {
      return { shouldShowEnjoy: false, shouldShowFeedback: false, activity: null };
    }

    const now = Date.now();
    const timeSinceSpinMs = now - spinData.timestamp;

    if (timeSinceSpinMs < this.ENJOY_DURATION_MS) {
      // Less than 10 minutes have passed, show enjoy overlay
      return { shouldShowEnjoy: true, shouldShowFeedback: false, activity: spinData.activity };
    } else {
      // More than 10 minutes have passed, show feedback modal directly
      return { shouldShowEnjoy: false, shouldShowFeedback: true, activity: spinData.activity };
    }
  }

  /**
   * Calculate remaining time for enjoy overlay
   */
  getRemainingTime(startTime: number): { remainingMs: number; remainingMinutes: number; remainingSeconds: number } {
    const now = Date.now();
    const elapsedMs = now - startTime;
    const remainingMs = Math.max(0, this.ENJOY_DURATION_MS - elapsedMs);
    const remainingMinutes = Math.ceil(remainingMs / (60 * 1000));
    const remainingSeconds = Math.ceil((remainingMs % (60 * 1000)) / 1000);

    return { remainingMs, remainingMinutes, remainingSeconds };
  }

  /**
   * Submit post-activity feedback
   */
  async submitPostActivityFeedback(
    activity: PostSpinActivity,
    experienceRating: string | null,
    comment: string,
    backendBaseUrl: string
  ): Promise<boolean> {
    try {
      const contextData = {
        experience_rating: experienceRating,
        activity_name: activity.name,
        activity_domain: activity.domain,
        activity_type: activity.type,
        challenge_rating: activity.base_challenge_rating,
        duration_range: activity.duration_range,
        feedback_timestamp: new Date().toISOString()
      };

      const response = await fetch(`${backendBaseUrl}/api/feedback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          feedback_type: 'post_activity_feedback',
          content_type: 'ActivityTailored',
          object_id: activity.activity_tailored_id || activity.id,
          user_comment: comment,
          criticality: 1,
          context_data: contextData,
          wheel_item_id: activity.wheel_item_id || activity.id
        })
      });

      if (response.ok) {
        console.log('✅ Post-activity feedback submitted successfully');
        this.clearStoredSpinData();
        return true;
      } else {
        console.error('❌ Failed to submit post-activity feedback');
        return false;
      }
    } catch (error) {
      console.error('❌ Error submitting post-activity feedback:', error);
      return false;
    }
  }

  /**
   * Submit activity cancellation feedback
   */
  async submitCancellationFeedback(
    activity: PostSpinActivity,
    reason: string,
    backendBaseUrl: string,
    currentUserId?: string
  ): Promise<boolean> {
    try {
      const response = await fetch(`${backendBaseUrl}/api/feedback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          feedback_type: 'activity_cancellation',
          content_type: 'wheelitem',
          object_id: activity.id || '',
          user_comment: reason,
          criticality: 2, // Medium criticality for cancellations
          context_data: {
            activity_name: activity.name,
            activity_domain: activity.domain,
            cancellation_reason: reason,
            cancellation_timestamp: new Date().toISOString(),
            user_profile_id: currentUserId
          }
        })
      });

      if (response.ok) {
        console.log('✅ Activity cancellation feedback submitted successfully');
        this.clearStoredSpinData();
        return true;
      } else {
        console.warn('⚠️ Failed to submit cancellation feedback:', response.status);
        return false;
      }
    } catch (error) {
      console.error('❌ Error submitting cancellation feedback:', error);
      return false;
    }
  }
}
